import { transformProduct, transformPromoCard } from './transformResults'

// A connector is the lowest level abstraction that InstantSearch.js provides.
// By exposing the AlgoliaSearch instance we can extract whatever methods or pieces of data that
// are held by the Algolia instance and expose them to any component to consume.
// The docs are very sparse
// https://www.algolia.com/doc/guides/building-search-ui/widgets/create-your-own-widgets/vue/
// This implementation is borrowed from
// https://github.com/algolia/instantsearch.js/blob/55313e4ea4105b777f3f102e9f48a7e440496d25/src/connectors/search-box/connectSearchBox.ts#L143
// and tweaked to merge the results and pass all the instance methods that we need to
// the component that will consume this connector

function sendEvent(instance, queryID) {
  return (eventType, item, eventName) => {
    // The connectors from Algolia chunk results so that it sends 20 items at max per call to thier servers as:
    // const hitsChunks = chunk(hitsArray);
    // see https://github.com/algolia/instantsearch.js/blob/69525bf2a3087aeb75c4f1e5ab8452012436f61f/src/lib/utils/createSendEventForHits.ts#L78-L138
    // and https://github.com/algolia/instantsearch.js/blob/master/src/connectors/hits/connectHits.ts
    const hits = [].concat(item)

    if (eventType === 'click') {
      instance.sendEventToInsights({
        eventType,
        hits: hits.map(hit => ({
          __position: hit.index + 1,
          __queryID: queryID,
          objectID: hit.objectID,
        })),
        insightsMethod: 'clickedObjectIDsAfterSearch',
        payload: {
          eventName,
          index: instance.indexName,
          objectIDs: hits.map(hit => hit.objectID),
          positions: hits.map(hit => hit.index + 1),
          queryID: queryID,
        },
      })
    }
  }
}

function toRenderState(opts) {
  const { helper, instantSearchInstance, results = {} } = opts
  const { state } = helper
  const setQueryAndSearch = query => {
    if (query !== state.query) {
      helper.setQuery(query).search()
    }
  }

  return {
    ...opts,
    refine: setQueryAndSearch,
    query: state.query || '',
    sendEvent: sendEvent(instantSearchInstance, results.queryID),
  }
}

// Algolia partitions results by index. We want to merge the 2 sets of results so we
// can feed a single array to the cards
function extractAndCombineResults(country, language, results) {
  const toHits = indexObj => indexObj.results?.hits || []
  const [products = [], promoCards = []] = results
  const transformedProducts = toHits(products)
    .map(transformProduct(country, language))
    .filter(i => Boolean(i.cartImageUrl))
  const transformedPromoCards = toHits(promoCards).map(transformPromoCard(language))
  return transformedProducts.concat(transformedPromoCards)
}

export const connectStateResults =
  (renderFn, unmountFn = () => {}) =>
  (widgetParams = {}) => {
    const { country, language } = widgetParams
    return {
      init(opts) {
        renderFn(
          {
            ...toRenderState(opts),
            widgetParams,
            items: [],
            hasResults: false,
            isSearchStalled: false,
          },
          true
        )
      },

      render(opts) {
        const { scopedResults = [], state = {}, searchMetadata = {} } = opts
        const items = extractAndCombineResults(country, language, scopedResults)

        renderFn(
          {
            ...opts,
            ...toRenderState(opts),
            widgetParams,
            items,
            hasResults: state.query && items.length,
            isSearchStalled: searchMetadata.isSearchStalled,
          },
          false
        )
      },

      dispose() {
        unmountFn()
      },
    }
  }
